import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios'
import Cookies from 'js-cookie'
import { GATSBY_MAIN_URL } from '../constants/env'

type Tokens = { access_token: string; refresh_token: string }

interface ConfigOptions {
  authorizationOff?: boolean
  unauthorized?: boolean
}

const authorizationBasicToken = `Basic ZXhhbXBsZUNsaWVudDpleGFtcGxlU2VjcmV0`

const refreshAccessToken = async () => {
  const refreshToken = Cookies.get('refresh_token')
  const dataReq = { refreshToken, grantType: 'refresh_token' }

  const { data } = await axios.post<Tokens>(GATSBY_MAIN_URL + '/security/api/v1/login', dataReq, {
    headers: {
      Authorization: authorizationBasicToken,
    },
  })

  const { refresh_token, access_token } = data

  if (access_token && refresh_token) {
    Cookies.set('app_access_token', access_token)
    Cookies.set('app_refresh_token', refresh_token)
    return access_token
  } else {
    Cookies.remove('app_access_token')
    Cookies.remove('app_refresh_token')
    Cookies.remove('app_user_email')
    Cookies.remove('app_user_freshchat_restoreId')
    throw new Error()
  }
}

class HttpService {
  private axios: AxiosInstance

  constructor() {
    this.axios = this.createInstance()

    this.setInterceptors()
  }

  private createInstance() {
    return axios.create({ baseURL: GATSBY_MAIN_URL, timeout: 300000 })
  }

  private onRequestFullfiled(config: AxiosRequestConfig<any> & ConfigOptions) {
    if (config.authorizationOff) return config

    const token = Cookies.get('app_access_token')

    config.headers = {
      ...config.headers,
      Authorization: config.unauthorized ? authorizationBasicToken : `Bearer ${token}`,
    }

    delete config.authorizationOff
    delete config.unauthorized

    return config
  }

  private async onResponseError(error: AxiosError<any>) {
    if (error?.response?.status !== 401) {
      return Promise.reject(error)
    }

    const originalRequest = error.config as AxiosRequestConfig<any> & { _retry: boolean }

    try {
      originalRequest._retry = true

      const access_token = await refreshAccessToken()

      const config = error?.response.config || {}
      config.headers = { ...(error?.response.config.headers || {}), Authorization: `Bearer ${access_token}` }

      return axios(config)
    } catch (error) {
      return Promise.reject(error)
    }
  }

  private setInterceptors() {
    this.axios.interceptors.request.use(this.onRequestFullfiled)
    this.axios.interceptors.response.use(response => response, this.onResponseError)
  }

  get<T>(path: string, config?: AxiosRequestConfig<object> & ConfigOptions) {
    return this.axios.get<object, T>(path, config)
  }
  post<T>(path: string, data?: object, config?: AxiosRequestConfig<object> & ConfigOptions) {
    return this.axios.post<object, T>(path, data, config)
  }
  patch<T>(path: string, data?: object, config?: AxiosRequestConfig<object>) {
    return this.axios.patch<object, T>(path, data, config)
  }
  delete<T>(path: string, data?: object) {
    return this.axios.delete<object, T>(path, data)
  }
}

export default new HttpService()
